import { Box, Divider, Skeleton, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { Field, Form, Formik } from "formik";
import moment from "moment-timezone";
import React from "react";
import * as Yup from "yup";
import LoadingOverlay from "../../../common/components/data-display/LoadingOverlay";
import FlowerQualitySelect from "../../../common/components/form/FlowerQualitySelect";
import FormFlowerColorPicker from "../../../common/components/form/FormFlowerColorPicker";
import { FormQuantityField } from "../../../common/components/form/FormQuantityField";
import FormSelect from "../../../common/components/form/FormSelect";
import { useAlerts } from "../../../common/context/AlertContext";
import { useCollective } from "../../../common/context/CollectiveContext";
import { FLOWER_QUALITY } from "../../../common/enums/FlowerQuality";
import {
  SALE_FOCUS,
  SALE_FOCUS_OPTIONS_NO_ADVANCED_ORDER,
} from "../../../common/enums/SalesFocus";
import { useLocalStorage } from "../../../common/hooks/useLocalStorage";
import BaseProductService from "../../../common/service/BaseProductService";
import StockLogService from "../../../common/service/StockLogService";
import { isPresaleUploadingAvailableForDate } from "../../../common/util/DatePickerUtilFrontend";
import { displayPrice } from "../../../common/util/DisplayUtil";
import ProductPrice from "../../AdvancedRequests/ProductPrice";

function getDefaultValues(baseProduct, productInstance, lastSaleFocus) {
  if (baseProduct) {
    return {
      attributes: {
        primaryColor: "",
        secondaryColor: "",
        quality: FLOWER_QUALITY.Standard,
      },
      saleFocus: lastSaleFocus || SALE_FOCUS.Speculation,
      name: baseProduct.name,
      quantity: "",
      baseProductId: baseProduct.id,
      estimatedUnitPrice: baseProduct.minPrice || baseProduct.maxPrice,
    };
  } else if (productInstance) {
    return {
      attributes: {
        primaryColor:
          productInstance.attributes.find(
            (attr) => attr.attributeName === "PrimaryColor"
          )?.attributeValue || "",
        secondaryColor:
          productInstance.attributes.find(
            (attr) => attr.attributeName === "SecondaryColor"
          )?.attributeValue || "",

        quality:
          productInstance.attributes.find(
            (attr) => attr.attributeName === "Quality"
          )?.attributeValue || FLOWER_QUALITY.Standard,
      },
      saleFocus: productInstance.saleFocus,
      name: productInstance.name,
      quantity: productInstance.quantity || "",
      baseProductId: productInstance.baseProductId,
      estimatedUnitPrice: productInstance.estimatedUnitPrice,
      dateStocked: productInstance.dateStocked,
    };
  }
}

export default function UploadInventoryDialog({
  isOpen,
  onClose,
  supplierBusiness,
  baseProduct,
  productInstance,
  onAdd,
  onUpdate,
  onDelete,
  marketDate,
}) {
  const [lastSaleFocus, setLastSaleFocus] = useLocalStorage(
    "LAST_SALE_FOCUS",
    SALE_FOCUS.Speculation
  );
  const { collectiveInfo } = useCollective();
  const { addErrorAlert } = useAlerts();
  const [baseProductDetails, setBaseProductDetails] = React.useState();
  const formikRef = React.useRef(null);
  const dateStocked = moment(marketDate || productInstance?.dateStocked);
  React.useEffect(() => {
    if ((productInstance || baseProduct) && formikRef.current) {
      formikRef.current.resetForm(
        getDefaultValues(baseProduct, productInstance, lastSaleFocus)
      );
    }
    if (baseProduct || productInstance) {
      BaseProductService.getBaseProductById(
        baseProduct?.id || productInstance?.baseProductId,
        true
      )
        .then((baseProductDetails) => {
          setBaseProductDetails(baseProductDetails);
        })
        .catch((err) => {
          addErrorAlert("Error getting base product details", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseProduct, productInstance, addErrorAlert]);

  const handleDelete = async () => {
    formikRef.current.setSubmitting(true);
    try {
      const updatedReq = await StockLogService.deleteStockLog(
        productInstance.id
      );
      onDelete(updatedReq);
    } catch (err) {
      addErrorAlert("Error deleting product", err);
    }
    formikRef.current.setSubmitting(false);
  };

  const handleSubmit = async (values) => {
    setLastSaleFocus(values.saleFocus);
    if (productInstance) {
      const stockToUpdate = {
        supplierId: supplierBusiness?.id || productInstance.supplier?.id,
        saleFocus: values.saleFocus,
        quantity: values.quantity,
        id: productInstance.id,
      };
      try {
        const stockLog = await StockLogService.updateStockLog(stockToUpdate);
        onUpdate(stockLog);
      } catch (e) {
        addErrorAlert("Error updating product", e);
      }
    } else {
      const stockToAdd = {
        saleFocus: values.saleFocus,
        supplierId: supplierBusiness.id,
        dateStocked: marketDate.format("YYYY-MM-DD"),
        productInstance: {
          name: values.name,
          baseProductId: values.baseProductId,
          instanceAttributes: [
            {
              attributeName: "PrimaryColor",
              attributeValue: values.attributes.primaryColor,
            },
            {
              attributeName: "SecondaryColor",
              attributeValue: values.attributes.secondaryColor,
            },
            {
              attributeName: "Quality",
              attributeValue: values.attributes.quality,
            },
          ],
        },
        quantity: values.quantity,
        estimatedUnitPrice: values.estimatedUnitPrice,
      };
      try {
        const stockLog = await StockLogService.addStockLog(stockToAdd);
        onAdd(stockLog);
      } catch (e) {
        addErrorAlert("Error adding product", e);
      }
    }
  };

  const harvestNotes = baseProductDetails?.attributes?.find(
    (attr) => attr.attributeName === "Harvest Stage"
  )?.value;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Formik
        initialValues={getDefaultValues(
          baseProduct,
          productInstance,
          lastSaleFocus
        )}
        validateOnBlur={false}
        onSubmit={handleSubmit}
        innerRef={formikRef}
        validationSchema={
          // Yup validation. Require positive quantity and a primary color
          Yup.object().shape({
            saleFocus: Yup.string()
              .required("Sale focus is required")
              .test(
                "presale-before-cutoff",
                `The window for market sales has closed for the ${dateStocked?.format("MMMM Do")} market`,
                (value) => {
                  return (
                    value !== SALE_FOCUS.PreSale ||
                    isPresaleUploadingAvailableForDate(
                      dateStocked,
                      collectiveInfo
                    )
                  );
                }
              ),
            quantity: Yup.number()
              .typeError("Must be a number")
              .required("Quantity is required")
              .positive("Enter a valid quantity")
              // When saleFocus is "Pre-Sale", then quantity must be at least 5
              .when("saleFocus", {
                is: SALE_FOCUS.PreSale,
                then: () =>
                  Yup.number()
                    .typeError("Must be a number")
                    .min(5, "Market Sales quantity must be at least 5"),
              }),
            attributes: Yup.object().shape({
              primaryColor: Yup.string().required("Primary color is required"),
            }),
            estimatedUnitPrice: Yup.number().required(
              "Error determining price; please try reloading the page."
            ),
          })
        }
      >
        {({ setFieldValue, values, isSubmitting }) => (
          <Form autoComplete="off">
            <DialogTitle>
              {baseProduct?.name || productInstance?.name}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ pt: 1, maxWidth: 250 }}>
                {Boolean(harvestNotes) && (
                  <Grid xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle1">Harvest Stage</Typography>
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {harvestNotes}
                      </Typography>
                    </Box>
                    <Divider />
                  </Grid>
                )}
                <Grid xs={12}>
                  <Field
                    component={FormSelect}
                    name="saleFocus"
                    label="Sale Focus"
                    options={SALE_FOCUS_OPTIONS_NO_ADVANCED_ORDER}
                    onChange={(e) => {
                      setFieldValue("saleFocus", e.target.value);
                      // Basic quality is only allowed for Speculation
                      if (
                        e.target.value !== SALE_FOCUS.Speculation &&
                        values.attributes.quality === FLOWER_QUALITY.Basic
                      ) {
                        setFieldValue(
                          "attributes.quality",
                          FLOWER_QUALITY.Standard
                        );
                      }
                    }}
                  />
                </Grid>
                {baseProductDetails ? (
                  <>
                    <Grid xs={12}>
                      <Field
                        component={FormFlowerColorPicker}
                        name="attributes.primaryColor"
                        label="Primary Color"
                        disabled={!!productInstance}
                      />
                    </Grid>
                    <Grid xs={12}>
                      <Field
                        component={FormFlowerColorPicker}
                        name="attributes.secondaryColor"
                        label="Secondary Color"
                        disabled={!!productInstance}
                      />
                    </Grid>
                    <Grid xs={12}>
                      <FlowerQualitySelect
                        name="attributes.quality"
                        disabled={!!productInstance}
                        productDetails={baseProductDetails}
                        // Basic quality is only allowed for Speculation
                        includeBasicQuality={values.saleFocus === "Speculation"}
                      />
                    </Grid>
                    <Grid xsOffset={6} xs={6}>
                      <Field
                        component={FormQuantityField}
                        name="quantity"
                        label="Quantity"
                      />
                    </Grid>
                    <Grid xs={12} textAlign="end">
                      <ProductPrice
                        name="estimatedUnitPrice"
                        baseProductId={
                          baseProduct?.id || productInstance?.baseProductId
                        }
                        priceGroups={baseProductDetails.priceGroups}
                        defaultPrice={baseProductDetails.defaultPrice}
                        fixedPrice={!!productInstance}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                  </>
                )}
                <Grid xs={12} textAlign="end">
                  <Typography>
                    {values.estimatedUnitPrice && values.quantity && (
                      <strong>
                        Total:{" "}
                        {displayPrice(
                          values.estimatedUnitPrice * values.quantity
                        )}
                      </strong>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: "space-between", position: "relative" }}
            >
              <Button onClick={onClose}>Cancel</Button>
              {productInstance && !productInstance.quantityFulfilled && (
                <Button color="error" onClick={handleDelete} variant="outlined">
                  Remove
                </Button>
              )}
              <Button color="primary" type="submit" variant="outlined">
                {!!productInstance ? `Update` : `Add`}
              </Button>
            </DialogActions>
            {isSubmitting && <LoadingOverlay />}
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
